import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';

interface ConfirmScreenProps {
    email: string;
    onConfirmed: () => void;
    /** Optional context message (e.g. when reached by signing in unverified). */
    notice?: string;
}

/**
 * Sign-up email verification: the supplier enters the code emailed by Cognito.
 */
export default function ConfirmScreen({ email, onConfirmed, notice }: ConfirmScreenProps) {
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [resending, setResending] = useState(false);
    const [resent, setResent] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        if (!code.trim()) {
            setError('Enter the verification code sent to your email.');
            return;
        }
        setSubmitting(true);
        try {
            await getAuthService().confirmSignUp(email, code.trim());
            setConfirmed(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Confirmation failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setResent(false);
        setResending(true);
        try {
            await getAuthService().resendSignUpCode(email);
            setResent(true);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not resend the code. Please try again.');
        } finally {
            setResending(false);
        }
    };

    if (confirmed) {
        return (
            <AuthCard title="Email confirmed" subtitle="Your registration is complete.">
                <SpaceBetween size="l">
                    <Alert type="success">
                        Your account is now awaiting administrator approval. You&apos;ll be able to
                        sign in once it&apos;s approved.
                    </Alert>
                    <Button variant="primary" onClick={onConfirmed}>
                        Back to sign in
                    </Button>
                </SpaceBetween>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Confirm your email" subtitle={`We sent a verification code to ${email}.`}>
            <SpaceBetween size="l">
                {notice ? <Alert type="warning">{notice}</Alert> : null}
                <FormField label="Verification code">
                    <Input
                        type="text"
                        value={code}
                        onChange={({ detail }) => setCode(detail.value)}
                    />
                </FormField>
                {error ? <Alert type="error">{error}</Alert> : null}
                {resent ? (
                    <Alert type="success">A new verification code has been sent to {email}.</Alert>
                ) : null}
                <Button variant="primary" loading={submitting} onClick={handleSubmit}>
                    Confirm
                </Button>
                <Button variant="link" loading={resending} onClick={handleResend}>
                    Didn&apos;t get a code? Resend
                </Button>
            </SpaceBetween>
        </AuthCard>
    );
}

ConfirmScreen.defaultProps = {
    notice: undefined,
};
